// components/SubmitAIToolForm.tsx
import React, { useState, useEffect, useRef } from 'react';

interface AIToolFormData {
  title: string;
  description: string;
  longDescription: string;
  toolUrl: string;
  icon: string;
  iconFile: File | null;
  isFree: boolean;
  tags: string[];
  category: string;
  pricingModel: string;
  customCategory: string;
  customPricing: string;
}

// Track which ads have been initialized
const adInitializationTracker = new Set<string>();

// JSON-LD schema for structured data
const generateStructuredData = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Submit AI Tool Form",
    "description": "Submit your AI tool to our directory. Get discovered by thousands of AI enthusiasts and professionals.",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "creator": {
      "@type": "Organization",
      "name": "AI Tools Directory",
      "url": "https://aitoolsdirectory.com"
    }
  };
};

// Types of ads to display:
const AD_TYPES = {
  BANNER: 'banner',
  MEDIUM_RECTANGLE: 'medium_rectangle',
  LARGE_RECTANGLE: 'large_rectangle',
  LEADERBOARD: 'leaderboard',
  HALF_PAGE: 'half_page',
  RESPONSIVE: 'responsive',
  NATIVE: 'native',
  IN_FEED: 'in_feed'
} as const;

type AdType = keyof typeof AD_TYPES;

const SubmitAIToolForm: React.FC = () => {
  const [formData, setFormData] = useState<AIToolFormData>({
    title: '',
    description: '',
    longDescription: '',
    toolUrl: '',
    icon: '🚀',
    iconFile: null,
    isFree: true,
    tags: [],
    category: '',
    pricingModel: '',
    customCategory: '',
    customPricing: ''
  });

  const [currentTag, setCurrentTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [showCustomPricing, setShowCustomPricing] = useState(false);
  const [iconPreview, setIconPreview] = useState<string>('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const adContainersRef = useRef<Map<string, HTMLDivElement>>(new Map());

  const categories = [
    'Self-Hosted AI Tools',
    'AI Writing Assistants',
    'Image Generation',
    'Code Assistants',
    'Video Creation',
    'Audio Processing',
    'Productivity AI',
    'Research & Analysis',
    'Marketing AI',
    'Development Tools'
  ];

  const pricingModels = [
    'Free',
    'Freemium',
    'Subscription',
    'One-time Purchase',
    'Open Source',
    'Enterprise'
  ];

  const popularIcons = ['🚀', '🤖', '🧠', '🔍', '🎨', '💻', '📊', '🔧'];

  // SEO optimized page title and description
  const pageTitle = "Submit AI Tool - Free Listing | AI Tools Directory";
  const pageDescription = "Submit your AI tool for free. Get featured in our directory and reach thousands of AI enthusiasts, developers, and businesses. Fast approval process.";

  // Initialize ads on component mount - WITHOUT calling push()
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Update document title and meta description for SEO
      document.title = pageTitle;
      
      // Check if description meta exists, if not create it
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        metaDescription.setAttribute('content', pageDescription);
        document.head.appendChild(metaDescription);
      }
      
      // Add structured data
      const existingStructuredData = document.querySelector('script[type="application/ld+json"]');
      if (!existingStructuredData) {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.text = JSON.stringify(generateStructuredData());
        document.head.appendChild(script);
      }
    }
    
    return () => {
      // No cleanup needed for ads - let AdSense handle them
    };
  }, []);

  // Safe ad initialization for a single ad
  const initializeAd = (slotId: string, container: HTMLDivElement) => {
    if (adInitializationTracker.has(slotId) || !container) return;
    
    try {
      // Mark as initialized
      adInitializationTracker.add(slotId);
      
      // Check if AdSense is loaded
      if (window.adsbygoogle) {
        // Only push if container has an ins element
        const insElements = container.getElementsByClassName('adsbygoogle');
        if (insElements.length > 0) {
          // Push once
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          console.log(`✅ Ad initialized: ${slotId}`);
        }
      }
    } catch (error) {
      console.warn(`⚠️ Ad initialization warning for ${slotId}:`, error);
    }
  };

  // Ad Component - Fixed version
  const AdSlot = ({ 
    slotId, 
    className = '', 
    type = AD_TYPES.RESPONSIVE,
    size = 'auto',
    customContent = null 
  }: { 
    slotId: string; 
    className?: string; 
    type?: string;
    size?: string;
    customContent?: React.ReactNode | null;
  }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // Initialize this specific ad when it mounts
    useEffect(() => {
      if (!containerRef.current) return;
      
      const container = containerRef.current;
      const adId = `ad-${slotId}-${Date.now()}`;
      
      // Clear container
      container.innerHTML = '';
      
      // Create unique ins element
      const insElement = document.createElement('ins');
      insElement.className = 'adsbygoogle';
      insElement.id = adId;
      insElement.style.display = 'block';
      insElement.style.overflow = 'hidden';
      
      // Set dimensions based on type - FIXED: Use string values from AD_TYPES
      switch (type) {
        case AD_TYPES.LEADERBOARD:
          insElement.style.minHeight = '90px';
          insElement.style.height = '90px';
          insElement.style.maxWidth = '728px';
          break;
        case AD_TYPES.MEDIUM_RECTANGLE:
        case AD_TYPES.LARGE_RECTANGLE:
          insElement.style.minHeight = '250px';
          insElement.style.height = '250px';
          insElement.style.maxWidth = type === AD_TYPES.MEDIUM_RECTANGLE ? '300px' : '336px';
          break;
        default:
          insElement.style.minHeight = '90px';
      }
      
      // Set attributes
      insElement.setAttribute('data-ad-client', 'ca-pub-4477070546327805');
      insElement.setAttribute('data-ad-slot', slotId);
      insElement.setAttribute('data-ad-format', 'auto');
      insElement.setAttribute('data-full-width-responsive', 'true');
      
      container.appendChild(insElement);
      
      // Store container reference
      adContainersRef.current.set(slotId, container);
      
      // Initialize ad with IntersectionObserver
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setTimeout(() => initializeAd(slotId, container), 100);
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );
      
      observer.observe(container);
      
      // Fallback initialization after 2 seconds
      const timeoutId = setTimeout(() => {
        if (!adInitializationTracker.has(slotId)) {
          initializeAd(slotId, container);
        }
        observer.disconnect();
      }, 2000);
      
      return () => {
        observer.disconnect();
        clearTimeout(timeoutId);
      };
      
    }, [slotId, type]);

    // For development, show ad placeholders
    if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
      return (
        <div className={`ad-slot ${className} bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-dashed border-blue-200 p-4 text-center my-4 rounded-lg`}>
          <div className="text-sm font-medium text-blue-700 mb-1">ADVERTISEMENT</div>
          {customContent || (
            <div className="space-y-2">
              <div className="h-24 bg-gradient-to-r from-blue-100 to-indigo-100 rounded flex items-center justify-center">
                <div className="text-center">
                  <div className="text-lg font-semibold text-blue-800">Promoted AI Tool</div>
                  <div className="text-sm text-blue-600">Try Our Premium AI Solutions</div>
                </div>
              </div>
              <div className="text-xs text-gray-500">
                Ad Slot: {slotId} | Type: {type} | Size: {size}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Production ad rendering - FIXED: Check for rectangle types
    const isRectangleAd = type === AD_TYPES.MEDIUM_RECTANGLE || type === AD_TYPES.LARGE_RECTANGLE;
    
    return (
      <div className={`ad-slot ${className}`}>
        <div className="text-xs text-gray-500 mb-1 text-center">Advertisement</div>
        <div 
          ref={containerRef}
          style={{ 
            minHeight: isRectangleAd ? '250px' : '90px',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden',
            margin: '0 auto'
          }}
          data-slot={slotId}
        />
      </div>
    );
  };

  // Simple validation function
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Tool name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    if (!formData.longDescription.trim()) {
      newErrors.longDescription = 'Detailed description is required';
    } else if (formData.longDescription.length < 50) {
      newErrors.longDescription = 'Detailed description must be at least 50 characters';
    }

    if (!formData.toolUrl.trim()) {
      newErrors.toolUrl = 'Website URL is required';
    } else if (!isValidUrl(formData.toolUrl)) {
      newErrors.toolUrl = 'Please enter a valid URL';
    }

    const finalCategory = showCustomCategory ? formData.customCategory : formData.category;
    if (!finalCategory.trim()) {
      newErrors.category = 'Category is required';
    }

    const finalPricing = showCustomPricing ? formData.customPricing : formData.pricingModel;
    if (!finalPricing.trim()) {
      newErrors.pricingModel = 'Pricing model is required';
    }

    if (formData.tags.length === 0) {
      newErrors.tags = 'Please add at least one tag';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string: string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleIconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, icon: 'Please upload an image file' }));
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, icon: 'Image must be less than 2MB' }));
        return;
      }

      setFormData(prev => ({ ...prev, iconFile: file, icon: '' }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setIconPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Clear any previous errors
      if (errors.icon) {
        setErrors(prev => ({ ...prev, icon: '' }));
      }
    }
  };

  const removeIcon = () => {
    setFormData(prev => ({ ...prev, iconFile: null, icon: '🚀' }));
    setIconPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddTag = () => {
    if (currentTag.trim() && !formData.tags.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }));
      setCurrentTag('');
      
      // Clear tags error
      if (errors.tags) {
        setErrors(prev => ({
          ...prev,
          tags: ''
        }));
      }
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent, type: 'tag') => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (type === 'tag') handleAddTag();
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === 'custom') {
      setShowCustomCategory(true);
      setFormData(prev => ({ ...prev, category: '', customCategory: '' }));
    } else {
      setShowCustomCategory(false);
      setFormData(prev => ({ ...prev, category: value, customCategory: '' }));
    }
    
    if (errors.category) {
      setErrors(prev => ({ ...prev, category: '' }));
    }
  };

  const handlePricingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === 'custom') {
      setShowCustomPricing(true);
      setFormData(prev => ({ ...prev, pricingModel: '', customPricing: '' }));
    } else {
      setShowCustomPricing(false);
      setFormData(prev => ({ ...prev, pricingModel: value, customPricing: '' }));
    }
    
    if (errors.pricingModel) {
      setErrors(prev => ({ ...prev, pricingModel: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Prepare final data
      const finalData = {
        ...formData,
        category: showCustomCategory ? formData.customCategory : formData.category,
        pricingModel: showCustomPricing ? formData.customPricing : formData.pricingModel,
        icon: formData.iconFile ? 'uploaded-icon' : formData.icon
      };

      console.log('Submitting:', finalData);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSubmitStatus('success');
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        longDescription: '',
        toolUrl: '',
        icon: '🚀',
        iconFile: null,
        isFree: true,
        tags: [],
        category: '',
        pricingModel: '',
        customCategory: '',
        customPricing: ''
      });
      setIconPreview('');
      setShowCustomCategory(false);
      setShowCustomPricing(false);
      
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (submitStatus === 'success') {
      const timer = setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  return (
    <>
      {/* Structured Data for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(generateStructuredData()) }} />
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Header with Native Ad */}
          <AdSlot 
            slotId="header_native_ad"
            type={AD_TYPES.NATIVE}
            className="mb-6"
          />
          
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl text-white">🚀</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Submit Your AI Tool - Free Listing
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Get your AI tool featured in our directory and reach thousands of AI enthusiasts, developers, and businesses. 
              <span className="font-semibold text-blue-600"> Free submission with fast approval!</span>
            </p>
          </div>

          {/* Responsive Banner Ad */}
          <AdSlot 
            slotId="form_top_banner"
            type={AD_TYPES.BANNER}
            className="mb-6"
          />

          {/* Success Message */}
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg shadow-sm transition-all duration-300 ease-in-out transform">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    Successfully Submitted!
                  </h3>
                  <p className="text-sm text-green-700 mt-1">
                    Your AI tool has been submitted for review. We'll notify you once it's published.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" aria-label="AI Tool Submission Form">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              
              {/* Tool Name */}
              <div className="mb-6">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                  Tool Name *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter your AI tool name (e.g., ChatGPT, Midjourney, GitHub Copilot)"
                  aria-required="true"
                  aria-describedby={errors.title ? "title-error" : undefined}
                />
                {errors.title && (
                  <p id="title-error" className="mt-1 text-sm text-red-600" role="alert">{errors.title}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Use a clear, descriptive name that includes relevant keywords
                </p>
              </div>

              {/* In-feed Ad between form sections */}
              <AdSlot 
                slotId="form_middle_feed"
                type={AD_TYPES.IN_FEED}
              />

              {/* Short Description */}
              <div className="mb-6">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Short Description *
                  <span className="text-xs text-gray-500 ml-1">(SEO optimized)</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Brief description that appears in tool listings and search results. Include main features and benefits."
                  aria-required="true"
                  aria-describedby={errors.description ? "description-error" : undefined}
                />
                {errors.description && (
                  <p id="description-error" className="mt-1 text-sm text-red-600" role="alert">{errors.description}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Tip: Include keywords like "AI tool", "machine learning", "artificial intelligence" for better SEO
                </p>
              </div>

              {/* Long Description */}
              <div className="mb-6">
                <label htmlFor="longDescription" className="block text-sm font-medium text-gray-700 mb-2">
                  Detailed Description *
                </label>
                <textarea
                  id="longDescription"
                  name="longDescription"
                  value={formData.longDescription}
                  onChange={handleInputChange}
                  rows={8}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.longDescription ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Comprehensive description of your tool. Include features, use cases, benefits, technical details, and target audience..."
                  aria-required="true"
                  aria-describedby={errors.longDescription ? "longDescription-error" : undefined}
                />
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>Minimum 50 characters (recommended: 200-500 for SEO)</span>
                  <span>{formData.longDescription.length}/2000</span>
                </div>
                {errors.longDescription && (
                  <p id="longDescription-error" className="mt-1 text-sm text-red-600" role="alert">{errors.longDescription}</p>
                )}
              </div>

              {/* Medium Rectangle Ad */}
              <AdSlot 
                slotId="form_middle_rectangle"
                type={AD_TYPES.MEDIUM_RECTANGLE}
                className="text-center"
              />

              {/* Website URL */}
              <div className="mb-6">
                <label htmlFor="toolUrl" className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL *
                </label>
                <input
                  type="url"
                  id="toolUrl"
                  name="toolUrl"
                  value={formData.toolUrl}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.toolUrl ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="https://your-ai-tool.com"
                  aria-required="true"
                  aria-describedby={errors.toolUrl ? "toolUrl-error" : undefined}
                />
                {errors.toolUrl && (
                  <p id="toolUrl-error" className="mt-1 text-sm text-red-600" role="alert">{errors.toolUrl}</p>
                )}
              </div>

              {/* Category & Pricing */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Category with custom option */}
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={showCustomCategory ? 'custom' : formData.category}
                    onChange={handleCategoryChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.category ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    aria-required="true"
                    aria-describedby={errors.category ? "category-error" : undefined}
                  >
                    <option value="">Select category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="custom">+ Add Custom Category</option>
                  </select>
                  
                  {showCustomCategory && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={formData.customCategory}
                        onChange={(e) => setFormData(prev => ({ ...prev, customCategory: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Enter custom category name"
                        aria-label="Custom category"
                      />
                    </div>
                  )}
                  {errors.category && (
                    <p id="category-error" className="mt-1 text-sm text-red-600" role="alert">{errors.category}</p>
                  )}
                </div>

                {/* Pricing Model with custom option */}
                <div>
                  <label htmlFor="pricingModel" className="block text-sm font-medium text-gray-700 mb-2">
                    Pricing Model *
                  </label>
                  <select
                    id="pricingModel"
                    name="pricingModel"
                    value={showCustomPricing ? 'custom' : formData.pricingModel}
                    onChange={handlePricingChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors.pricingModel ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    aria-required="true"
                    aria-describedby={errors.pricingModel ? "pricingModel-error" : undefined}
                  >
                    <option value="">Select pricing model</option>
                    {pricingModels.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                    <option value="custom">+ Custom Pricing</option>
                  </select>
                  
                  {showCustomPricing && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={formData.customPricing}
                        onChange={(e) => setFormData(prev => ({ ...prev, customPricing: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="e.g., Free trial, Contact for pricing, Pay-per-use, etc."
                        aria-label="Custom pricing"
                      />
                    </div>
                  )}
                  {errors.pricingModel && (
                    <p id="pricingModel-error" className="mt-1 text-sm text-red-600" role="alert">{errors.pricingModel}</p>
                  )}
                </div>
              </div>

              {/* Tags - SEO optimized */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags * <span className="text-xs text-gray-500">(SEO keywords)</span>
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={currentTag}
                    onChange={(e) => setCurrentTag(e.target.value)}
                    onKeyPress={(e) => handleKeyPress(e, 'tag')}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Add relevant tags (AI, chatbot, machine learning, natural language processing, etc.)"
                    aria-label="Add tags"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    aria-label="Add tag"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 min-h-8">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="text-blue-600 hover:text-blue-800 focus:outline-none"
                        aria-label={`Remove tag ${tag}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                {errors.tags && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.tags}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Add relevant keywords for better discoverability. Common tags: AI, machine learning, chatbot, GPT, automation
                </p>
              </div>

              {/* Native Ad before icon section */}
              <AdSlot 
                slotId="before_icon_native"
                type={AD_TYPES.NATIVE}
              />

              {/* Icon Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tool Icon
                </label>
                
                {/* Upload Section */}
                <div className="mb-4">
                  <label className="block text-sm text-gray-600 mb-2">Upload Custom Icon</label>
                  <div className="flex items-center gap-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleIconUpload}
                      className="hidden"
                      id="icon-upload"
                      aria-label="Upload icon"
                    />
                    <label
                      htmlFor="icon-upload"
                      className="cursor-pointer px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors text-center"
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                    >
                      <div className="text-gray-600">
                        <svg className="w-6 h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm">Choose Image</span>
                      </div>
                    </label>
                    {iconPreview && (
                      <div className="flex items-center gap-2">
                        <img 
                          src={iconPreview} 
                          alt="Icon preview" 
                          className="w-12 h-12 rounded-lg object-cover"
                          loading="lazy"
                        />
                        <button
                          type="button"
                          onClick={removeIcon}
                          className="text-red-600 hover:text-red-800 text-sm"
                          aria-label="Remove icon"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB. Optimized for web display. Or select from emojis below.</p>
                  {errors.icon && (
                    <p className="mt-1 text-sm text-red-600" role="alert">{errors.icon}</p>
                  )}
                </div>

                {/* Emoji Selection */}
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Or Select Emoji</label>
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                    {popularIcons.map(icon => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, icon, iconFile: null }));
                          setIconPreview('');
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                        className={`p-3 text-2xl rounded-lg border-2 transition-all ${
                          formData.icon === icon && !formData.iconFile
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        aria-label={`Select ${icon} emoji`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Free Checkbox */}
              <div className="flex items-center mb-6">
                <input
                  type="checkbox"
                  id="isFree"
                  name="isFree"
                  checked={formData.isFree}
                  onChange={(e) => setFormData(prev => ({ ...prev, isFree: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  aria-label="Tool is free to use"
                />
                <label htmlFor="isFree" className="ml-2 text-sm text-gray-700">
                  This tool is free to use
                </label>
              </div>

              {/* Banner Ad before submit button */}
              <AdSlot 
                slotId="before_submit_banner"
                type={AD_TYPES.BANNER}
              />

              {/* Submit Button */}
              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                aria-label={isSubmitting ? "Submitting AI tool..." : "Submit AI Tool"}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Submit AI Tool - Free Listing'
                )}
              </button>
            </div>
          </form>

          {/* Footer Ad Section */}
          <div className="mt-8">
            <AdSlot 
              slotId="footer_leaderboard"
              type={AD_TYPES.LEADERBOARD}
              className="mb-6"
            />
            
            {/* Footer SEO Content with Native Ad */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="p-6 bg-white rounded-lg border border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">Why Submit Your AI Tool?</h2>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span><strong>Free Exposure:</strong> Reach thousands of AI enthusiasts and professionals</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span><strong>SEO Benefits:</strong> Get backlinks and improve your search rankings</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span><strong>Fast Approval:</strong> Most tools are reviewed within 24 hours</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span><strong>Quality Traffic:</strong> Targeted visitors interested in AI solutions</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div>
                <AdSlot 
                  slotId="sidebar_native"
                  type={AD_TYPES.NATIVE}
                />
              </div>
            </div>
          </div>

          {/* Footer Note */}
          <p className="text-center text-gray-500 text-sm mt-6">
            Your tool will be reviewed within 24-48 hours before being published. 
            <span className="block text-xs mt-1">By submitting, you agree to our Terms of Service and Privacy Policy.</span>
          </p>
        </div>
      </div>
    </>
  );
};

export default SubmitAIToolForm;